import { ClockIcon, ExternalLinkIcon, FilterIcon } from "@heroicons/react/outline";
import { jsonrepair } from "jsonrepair";
import { FC, ReactNode, useCallback } from "react";

import Exceptions from "./Exception";
import { usePromptLayerToolLink } from "./hooks/usePromptLayerToolLink";
import SpanTabs from "./SpanTabs";
import KeyValueTable from "./SpanTabs/KeyValueTable";
import { toKeyValueRows } from "./SpanTabs/utils";
import { SpanIcon } from "./SpanIcon";
import { StatusCellBadge } from "./StatusCellRenderer";
import {
  formatDate,
  formatDurationLabel,
  formatSpanTypeLabel,
  getSpanDisplayName,
  getSpanDisplayType,
  getSpanRenderedToolAttributeKeys,
  getSpanToolInput,
  getSpanToolOutput,
} from "./utils";

import HistoryPageNavbar from "@/components/ContentNavbar/HistoryPageNavbar/index";
import LoadingSpinner from "@/components/LoadingSpinner";
import RequestDisplay from "@/components/RequestDisplay";
import { RequestDisplaySource } from "@/components/RequestDisplay/RequestDisplayUtils";
import {
  ADD_TRACES_STRUCTURED_FILTER_EVENT,
  type AddTracesStructuredFilterEventDetail,
  OPEN_TRACES_DRAWER_EVENT,
  useSidebarTab,
} from "@/components/SidebarSearchLayout/sidebar-tab-context";
import {
  calculateSpanStatuses,
  SpanStatus,
} from "@/components/TraceDetail/utils/calculateSpanStatus";
import { Badge, BadgeSize, BadgeVariant } from "@/components/ui/Badge";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRequest, useRequestBulk } from "@/queries";
import type { SpanDetailsProps } from "@/components/TraceDetail/SpanDetails/types";

const RequestContent: FC<{
  isLoading: boolean;
  requestPreview: { data: RequestDisplaySource | undefined };
  requestId: string;
  requestBulk: { data: RequestDisplaySource | undefined };
}> = ({ isLoading, requestPreview, requestId, requestBulk }) => {
  if (isLoading) {
    return <LoadingSpinner size={5} />;
  }

  if (!requestPreview.data) {
    return <p className="text-gray-500">No request log data available</p>;
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="shrink-0 border-b border-gray-200 bg-white">
        <HistoryPageNavbar requestId={requestId} requestInfo={requestPreview.data} compactTitle />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden pt-4">
        <RequestDisplay
          requestId={requestId}
          requestPreview={requestPreview.data}
          requestBulk={requestBulk.data}
        />
      </div>
    </div>
  );
};

const SPAN_ATTRIBUTES_FILTER_FIELD = "span_attributes";
const SPAN_RESOURCE_FILTER_FIELD = "span_resource";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const mergeNestedObjects = (
  target: Record<string, unknown>,
  source: Record<string, unknown>,
): Record<string, unknown> => {
  const result = { ...target };

  for (const [key, value] of Object.entries(source)) {
    const existingValue = result[key];
    if (isPlainObject(existingValue) && isPlainObject(value)) {
      result[key] = mergeNestedObjects(existingValue, value);
    } else {
      result[key] = value;
    }
  }

  return result;
};

const nestDottedObjectKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(nestDottedObjectKeys);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  const result: Record<string, unknown> = {};

  for (const [rawKey, rawValue] of Object.entries(value)) {
    const normalizedValue = nestDottedObjectKeys(rawValue);
    const path = rawKey.split(".");

    if (path.length === 1) {
      const existingValue = result[rawKey];
      result[rawKey] =
        isPlainObject(existingValue) && isPlainObject(normalizedValue)
          ? mergeNestedObjects(existingValue, normalizedValue)
          : normalizedValue;
      continue;
    }

    let currentLevel = result;
    for (const [index, segment] of path.entries()) {
      const isLeaf = index === path.length - 1;
      if (isLeaf) {
        const existingValue = currentLevel[segment];
        currentLevel[segment] =
          isPlainObject(existingValue) && isPlainObject(normalizedValue)
            ? mergeNestedObjects(existingValue, normalizedValue)
            : normalizedValue;
        continue;
      }

      const nextLevel = currentLevel[segment];
      if (!isPlainObject(nextLevel)) {
        currentLevel[segment] = {};
      }
      currentLevel = currentLevel[segment] as Record<string, unknown>;
    }
  }

  return result;
};

const RawDataSection = ({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: ReactNode;
}) => (
  <section>
    <div className="mb-1 flex items-center justify-between">
      <h5 className="text-sm font-medium text-gray-900">{title}</h5>
      <span className="text-xs text-gray-500">
        {count} {count === 1 ? "value" : "values"}
      </span>
    </div>
    {children}
  </section>
);

const DetailField = ({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}) => (
  <div>
    <p className="text-xs font-medium text-gray-500">{label}</p>
    <div className={cn("mt-1 break-words text-sm text-gray-900", mono && "font-mono text-[13px]")}>
      {value}
    </div>
  </div>
);

const getMetadataFilterValue = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === "string") {
    return value.trim() ? value.trim() : null;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
};

const dispatchAddTracesStructuredFilter = (detail: AddTracesStructuredFilterEventDetail) => {
  window.setTimeout(() => {
    window.dispatchEvent(new CustomEvent(ADD_TRACES_STRUCTURED_FILTER_EVENT, { detail }));
  }, 0);
};

const MetadataFilterButton = ({
  fieldPath,
  value,
  onAddToFilters,
}: {
  fieldPath: string;
  value: unknown;
  onAddToFilters: (fieldName: string, value: unknown) => void;
}) => {
  const filterValue = getMetadataFilterValue(value);

  if (!filterValue) {
    return null;
  }

  return (
    <BasicTooltip content="Add to trace filters">
      <Button
        type="button"
        variant="outline"
        size="tinyButton"
        className="ml-2 h-5 gap-1 px-1.5 text-[10px]"
        onClick={() => onAddToFilters(fieldPath, value)}
      >
        <FilterIcon className="h-3 w-3" />
      </Button>
    </BasicTooltip>
  );
};

const SpanDetails: FC<SpanDetailsProps> = ({
  selectedSpan,
  traceInput,
  traceFinalOutput,
  traceEntities = [],
  traceRunning = false,
  onJumpToSpan,
}) => {
  const sidebarTab = useSidebarTab();
  const openTracesDrawer = useCallback(() => {
    sidebarTab?.setActiveTab("Traces");
    window.dispatchEvent(new Event(OPEN_TRACES_DRAWER_EVENT));
  }, [sidebarTab]);

  const parseJsonString = useCallback((jsonString: string) => {
    try {
      return JSON.parse(jsonrepair(jsonString));
    } catch {
      return null;
    }
  }, []);

  const deepParseJsonStrings = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data: any): any => {
      if (typeof data === "string") {
        const parsed = parseJsonString(data);
        if (parsed !== null && typeof parsed === "object") {
          return deepParseJsonStrings(parsed);
        }
        return data;
      }
      if (Array.isArray(data)) {
        return data.map(deepParseJsonStrings);
      }
      if (typeof data === "object" && data !== null) {
        const result: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(data)) {
          result[key] = deepParseJsonStrings(value);
        }
        return result;
      }
      return data;
    },
    [parseJsonString],
  );

  const normalizeJsonData = useCallback(
    (content: unknown, nestDottedKeys = false) => {
      const normalizedData = deepParseJsonStrings(content);
      return nestDottedKeys ? nestDottedObjectKeys(normalizedData) : normalizedData;
    },
    [deepParseJsonStrings],
  );

  const handleAddMetadataFilter = useCallback(
    (fieldName: string, value: unknown) => {
      const filterValue = getMetadataFilterValue(value);
      if (!fieldName || !filterValue) {
        return;
      }

      openTracesDrawer();
      dispatchAddTracesStructuredFilter({
        field: SPAN_ATTRIBUTES_FILTER_FIELD,
        nestedKey: fieldName,
        value: filterValue,
        valueDisplay: filterValue,
      });
    },
    [openTracesDrawer],
  );

  const handleAddResourceFilter = useCallback(
    (fieldName: string, value: unknown) => {
      const filterValue = getMetadataFilterValue(value);
      if (!fieldName || !filterValue) {
        return;
      }

      openTracesDrawer();
      dispatchAddTracesStructuredFilter({
        field: SPAN_RESOURCE_FILTER_FIELD,
        nestedKey: fieldName,
        value: filterValue,
        valueDisplay: filterValue,
      });
    },
    [openTracesDrawer],
  );

  const toolLink = usePromptLayerToolLink(selectedSpan);

  if (!selectedSpan) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500">
        <p>Select a span to view its details</p>
      </div>
    );
  }

  const renderAttributes = (attributes: Record<string, unknown>) => {
    const renderedToolAttributeKeys = getSpanRenderedToolAttributeKeys(selectedSpan);
    const filteredAttributes = Object.entries(attributes).filter(([key]) => {
      if (key === "workspace_id") {
        return false;
      }
      if (renderedToolAttributeKeys.has(key)) {
        return false;
      }
      return true;
    });
    const hasAttributes = filteredAttributes.length > 0;

    if (!hasAttributes) {
      return null;
    }

    return (
      <RawDataSection title="Span attributes" count={filteredAttributes.length}>
        <KeyValueTable
          rows={toKeyValueRows(normalizeJsonData(Object.fromEntries(filteredAttributes), true))}
          renderActions={row => (
            <MetadataFilterButton
              fieldPath={row.path.join(".")}
              value={row.value}
              onAddToFilters={handleAddMetadataFilter}
            />
          )}
        />
      </RawDataSection>
    );
  };

  const renderRequestLog = () => {
    if (!selectedSpan.request_log_id) {
      return null;
    }

    const requestId = selectedSpan.request_log_id.toString();

    return <RequestLog requestId={requestId} />;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderResource = (resource: Record<string, any>) => {
    const hasResource = Object.keys(resource).length > 0;

    if (!hasResource) {
      return null;
    }

    return (
      <RawDataSection title="Span resource" count={Object.keys(resource).length}>
        <KeyValueTable
          rows={toKeyValueRows(normalizeJsonData(resource, true))}
          renderActions={row => (
            <MetadataFilterButton
              fieldPath={row.path.join(".")}
              value={row.value}
              onAddToFilters={handleAddResourceFilter}
            />
          )}
        />
      </RawDataSection>
    );
  };

  const spanStatus = calculateSpanStatuses([selectedSpan]).get(selectedSpan.span_id) ?? "Success";
  const spanType = getSpanDisplayType(selectedSpan);
  const formattedSpanType = spanType ? formatSpanTypeLabel(spanType) : null;

  const renderSpanInformation = () => {
    if (selectedSpan.request_log_id) {
      return null;
    }

    // Stamps beat function_input/output: promoted session spans carry both, and the
    // stamp is the run's truth while function_* is node machinery (args/kwargs)
    const spanInput =
      traceInput?.preview ??
      selectedSpan.attributes?.["input.value"] ??
      getSpanToolInput(selectedSpan);
    const spanOutput =
      traceFinalOutput?.preview ??
      selectedSpan.attributes?.["output.value"] ??
      getSpanToolOutput(selectedSpan);

    const spanDurationMs = selectedSpan.end
      ? new Date(selectedSpan.end).getTime() - new Date(selectedSpan.start).getTime()
      : null;

    const header = (
      <>
        <section>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <SpanIcon
                  spanNode={selectedSpan}
                  isPromptLayerTool={Boolean(toolLink)}
                  className="ml-0 h-6 w-6 flex-shrink-0"
                />
                <OverflowDisplayTooltip
                  content={getSpanDisplayName(selectedSpan)}
                  className="min-w-0"
                >
                  <h2 className="truncate text-[20px] font-semibold tracking-tight text-gray-900">
                    {getSpanDisplayName(selectedSpan)}
                  </h2>
                </OverflowDisplayTooltip>
              </div>
              {selectedSpan.name !== getSpanDisplayName(selectedSpan) ? (
                <p className="mt-1 break-words text-sm text-gray-500">{selectedSpan.name}</p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              {toolLink ? (
                <BasicTooltip content={`Open PromptLayer tool: ${toolLink.toolName}`}>
                  <a
                    href={toolLink.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex shrink-0 items-center gap-1 text-sm text-gray-800 hover:text-gray-500"
                  >
                    <ExternalLinkIcon className="h-4 w-4" />
                    Open Tool
                  </a>
                </BasicTooltip>
              ) : null}
              <StatusCellBadge status={spanStatus as SpanStatus} />
              {formattedSpanType ? (
                <Badge variant={BadgeVariant.GRAY} size={BadgeSize.SM}>
                  {formattedSpanType}
                </Badge>
              ) : null}
              {spanDurationMs !== null ? (
                <Badge variant={BadgeVariant.GRAY} size={BadgeSize.SM}>
                  <span className="inline-flex items-center gap-1">
                    <ClockIcon className="h-3.5 w-3.5" />
                    {formatDurationLabel(spanDurationMs)}
                  </span>
                </Badge>
              ) : null}
            </div>
          </div>
          <div className="mt-6 grid gap-x-8 gap-y-4 border-t border-gray-200 pt-4 sm:grid-cols-2 xl:grid-cols-4">
            <DetailField label="Span ID" value={selectedSpan.span_id} mono />
            <DetailField label="Parent ID" value={selectedSpan.parent_id || "None"} mono />
            <DetailField label="Start Time" value={formatDate(selectedSpan.start)} />
            <DetailField
              label="End Time"
              value={selectedSpan.end ? formatDate(selectedSpan.end) : "Running"}
            />
          </div>
        </section>
        <Exceptions span={selectedSpan} />
      </>
    );

    return (
      <SpanTabs
        span={selectedSpan}
        header={header}
        input={spanInput}
        output={spanOutput}
        outputTitle={traceFinalOutput && traceRunning ? "Latest output" : "Output"}
        traceEntities={traceEntities}
        inputVariableChips={Boolean(traceInput)}
        inputSourceSpanId={traceInput?.span_id}
        outputSourceSpanId={traceFinalOutput?.span_id}
        rawData={
          <div className="flex flex-col gap-y-5">
            {renderAttributes(selectedSpan.attributes)}
            {renderResource(selectedSpan.resource)}
          </div>
        }
        onJumpToSpan={onJumpToSpan}
      />
    );
  };

  return (
    <div className={`h-full ${selectedSpan.request_log_id ? "overflow-hidden" : "overflow-auto"}`}>
      {renderSpanInformation()}
      {renderRequestLog()}
    </div>
  );
};

function RequestLog({ requestId }: { requestId: string }) {
  const requestPreview = useRequest(requestId);
  const requestBulk = useRequestBulk(requestId);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <RequestContent
        isLoading={requestPreview.isLoading}
        requestPreview={requestPreview}
        requestId={requestId}
        requestBulk={requestBulk}
      />
    </div>
  );
}

export default SpanDetails;
export { NameInnerRenderer } from "./NameInnerRenderer";
export { StatusCellBadge } from "./StatusCellRenderer";
